import { CircularProgress, Grid } from "@material-ui/core";
import {
  Box,
  Button,
  DatePicker,
  Editor,
  Field,
  Form,
  Notification,
  Select,
  TextField,
} from "components";
import { CKEditor } from "components/common/CKEditor";
import { EnumHospitalType } from "constants/enums/hospital_types";
import { PERMISSIONS } from "constants/enums/permissions";
import {
  compose,
  withAuthorize,
  withFormik,
  withHooks,
  withTranslation,
} from "enhancers";
import withPreventLeaveDirtyForm from "enhancers/withPreventLeaveDirtyForm";
import { PageContent } from "layouts";
import {
  createYupDateObject,
  getErrorMessage,
  gql,
  homePath,
  notifyError,
  paths,
  Yup,
} from "utils/helper";
import { PARTNER_HOSPITAL_API } from ".";

export const PartnerHospitalDetailPage = (props: any) => {
  if (props.isLoading) {
    return (
      <PageContent
        title="รายละเอียดโรงพยาบาลพันธมิตร"
        breadcrumbs={props.breadcrumbs}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={400}
        >
          <CircularProgress />
        </Box>
      </PageContent>
    );
  }

  return (
    <PageContent
      title="รายละเอียดโรงพยาบาลพันธมิตร"
      breadcrumbs={props.breadcrumbs}
    >
      <Form>
        <Grid container spacing={5}>
          <Grid item xs={12} md={8}>
            <Field
              component={TextField}
              name="name"
              label="ชื่อโรงพยาบาล"
              required
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Field
              component={Select}
              name="hospitalType"
              label="ประเภท"
              required
              fullWidth
              disableClearable
              options={[
                {
                  label: props.t(`.${EnumHospitalType.GOVERNMENT}`),
                  value: EnumHospitalType.GOVERNMENT,
                },
                {
                  label: props.t(`.${EnumHospitalType.NON_GOVERNMENT}`),
                  value: EnumHospitalType.NON_GOVERNMENT,
                },
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <Field
              component={TextField}
              name="description"
              label="คำอธิบาย"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Field
              component={DatePicker}
              name="startEffectiveDate"
              label="วันที่เริ่มใช้สิทธิ์ได้ตั้งแต่"
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Field
              component={DatePicker}
              name="endEffectiveDate"
              label="จนถึงวันที่"
              fullWidth
              clearable
            />
          </Grid>

          <Grid item xs={12}>
            <Field
              component={Editor}
              name="termAndCondition"
              label="ข้อตกลงและเงื่อนไข"
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-start" mt={2}>
              <Button
                variant="outlined"
                onClick={props.handleCancel}
                style={{ marginRight: 16 }}
              >
                {props.t(".cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!props.enabledSubmitButton}
              >
                {props.t(".save")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Form>
    </PageContent>
  );
};

export const API = {
  FETCH_PARTNER_HOSPITAL: gql`
    query FETCH_PARTNER_HOSPITAL($id: String!) {
      partnerHospitalDetail(id: $id) {
        id
        name
        hospitalType
        description
        startEffectiveDate
        endEffectiveDate
        termAndCondition
      }
    }
  `,

  CREATE_PARTNER_HOSPITAL: gql`
    mutation CREATE_PARTNER_HOSPITAL(
      $name: String!
      $hospitalType: String!
      $description: String
      $startEffectiveDate: DateTime!
      $endEffectiveDate: DateTime
      $termAndCondition: String
    ) {
      createPartnerHospital(
        input: {
          name: $name
          hospitalType: $hospitalType
          description: $description
          startEffectiveDate: $startEffectiveDate
          endEffectiveDate: $endEffectiveDate
          termAndCondition: $termAndCondition
        }
      ) {
        id
      }
    }
  `,

  UPDATE_PARTNER_HOSPITAL: gql`
    mutation UPDATE_PARTNER_HOSPITAL(
      $id: String!
      $name: String!
      $hospitalType: String!
      $description: String
      $startEffectiveDate: DateTime!
      $endEffectiveDate: DateTime
      $termAndCondition: String
    ) {
      updatePartnerHospital(
        input: {
          id: $id
          name: $name
          hospitalType: $hospitalType
          description: $description
          startEffectiveDate: $startEffectiveDate
          endEffectiveDate: $endEffectiveDate
          termAndCondition: $termAndCondition
        }
      ) {
        id
      }
    }
  `,
};

const enhancer = compose(
  withFormik({
    validationSchema: Yup.object().shape({
      startEffectiveDate: createYupDateObject(
        "startEffectiveDate",
        "endEffectiveDate",
        "LTE"
      ),
      endEffectiveDate: createYupDateObject(
        "endEffectiveDate",
        "startEffectiveDate",
        "GTE"
      ),
      name: Yup.string().required("ต้องไม่เว้นว่างไว้"),
      hospitalType: Yup.string().required("ต้องไม่เว้นว่างไว้").nullable(),
      termAndCondition: Yup.string().required("ต้องไม่เว้นว่างไว้"),
    }),
  }),
  withPreventLeaveDirtyForm(),
  withAuthorize({
    permissions: [PERMISSIONS.PARTNER_MANAGEMENT_EDIT],
  }),
  withTranslation({ prefix: "pages.main.partnerHospitals.detail" }),
  withHooks((props: any, hooks: any) => {
    const { setFieldValue, t, match, setInitialValues, dirty, isValid } = props;
    const {
      useMutation,
      useMemo,
      useHandleSubmit,
      useCallback,
      useQuery,
      useEffect,
      useState,
    } = hooks;

    const isEditMode = !!match.params.id;
    const partnerId = match.params.id;
    const [initialValuesSet, setInitialValuesSet] = useState(false);

    const { data: partnerHospitalData, loading } = useQuery(
      API.FETCH_PARTNER_HOSPITAL,
      {
        variables: { id: partnerId },
        skip: !isEditMode,
      }
    );

    const [createPartnerHospital] = useMutation(API.CREATE_PARTNER_HOSPITAL, {
      onCompleted: () => {
        Notification.success(t(".saveSuccess"));
        paths.partnerHospitalPath().push();
      },
      refetchQueries: [
        {
          query: PARTNER_HOSPITAL_API.FETCH_PARTNER_HOSPITALS,
        },
      ],
    });
    const [updatePartnerHospital] = useMutation(API.UPDATE_PARTNER_HOSPITAL, {
      onCompleted: () => {
        Notification.success(t(".saveSuccess"));
        paths.partnerHospitalPath().push();
      },
      refetchQueries: [
        {
          query: API.FETCH_PARTNER_HOSPITAL,
          variables: { id: partnerId },
        },
        {
          query: PARTNER_HOSPITAL_API.FETCH_PARTNER_HOSPITALS,
        },
      ],
    });

    const partnerHospital = useMemo(() => {
      return partnerHospitalData?.partnerHospitalDetail;
    }, [partnerHospitalData]);

    useEffect(() => {
      if (partnerHospital && isEditMode && !loading) {
        setFieldValue("termAndCondition", partnerHospital.termAndCondition);
        setInitialValues(partnerHospital);
        setInitialValuesSet(true);
      }
    }, [partnerHospital, isEditMode, loading, setInitialValues, setFieldValue]);

    useEffect(() => {
      if (!isEditMode) {
        setInitialValuesSet(true);
      }
    }, [isEditMode]);

    const isLoading = isEditMode && (loading || !initialValuesSet);

    const title =
      initialValuesSet && partnerHospital
        ? partnerHospital.name
        : t(".addPartnerHospital");

    const breadcrumbs = useMemo(() => {
      return [
        { path: homePath(), label: t(".home") },
        { path: paths.partnerHospitalPath(), label: t(".partnerHospital") },
        { path: null, label: title },
      ];
    }, [title, t]);

    useHandleSubmit(
      async (values: any) => {
        if (isEditMode) {
          await updatePartnerHospital({
            variables: { id: partnerId, ...values },
          });
        } else {
          await createPartnerHospital({
            variables: { ...values },
          });
        }
      },
      [createPartnerHospital, updatePartnerHospital, isEditMode, partnerId]
    );

    const enabledSubmitButton = dirty && isValid;

    const handleCancel = useCallback(() => {
      paths.partnerHospitalPath().push();
    }, []);

    return {
      title,
      breadcrumbs,
      enabledSubmitButton,
      handleCancel,
      isEditMode,
      partnerHospital,
      isLoading,
      t,
    };
  })
);

export default enhancer(PartnerHospitalDetailPage);
